import "rsuite/dist/rsuite.css";
import React, { useState, useEffect } from 'react';
import axios from '../axios';
import { Row, Col, Table, Button, Form, Modal, Alert } from 'react-bootstrap';
import { DatePicker } from 'rsuite';

const toDate = (s) => (s ? new Date(`${String(s).slice(0, 10)}T00:00:00`) : null);
const toYmd = (d) => {
    if (!d) return null;
    const x = new Date(d);
    return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};

// Edit a purchase file's header and lines. The server enforces the rules once goods are received
// (vendor locked; a received line keeps its item, size and unit, can't be removed or go below what came in).
function EditPurchaseModal({ show, onHide, purchase, lines, hasReceived, onSaved }) {
    const isPaper = purchase.PurchaseCategory === 'Direct';
    const isImport = !!purchase.IsForeign || purchase.PurchaseType === 'Import';

    const [form, setForm] = useState({});
    const [rows, setRows] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [gsmOptions, setGsmOptions] = useState([]);
    const [materialOptions, setMaterialOptions] = useState([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!show) return;
        setError('');
        setForm({
            vendorId: String(purchase.VendorID), podate: toDate(purchase.PODate), currency: purchase.Currency || 'BDT',
            paymentTerm: purchase.PaymentTerm || '', incoterm: purchase.Incoterm || '', portOfLoading: purchase.PortOfLoading || '',
            portOfDischarge: purchase.PortOfDischarge || '', neededByDate: toDate(purchase.NeededByDate),
            expectedArrivalDate: toDate(purchase.ExpectedArrivalDate), remarks: purchase.Remarks || '',
            transportCost: parseFloat(purchase.TransportCost) ? String(parseFloat(purchase.TransportCost)) : '',
        });
        setRows(lines.map(l => ({
            lineId: l.LineID, gsmId: l.GSMID ? String(l.GSMID) : '', widthInch: l.WidthInch ? String(Number(l.WidthInch)) : '',
            materialId: l.MaterialID ? String(l.MaterialID) : '', description: l.Description || '',
            qty: String(Number(l.Qty)), unit: l.Unit || '', unitPrice: String(Number(l.UnitPrice)),
            received: parseFloat(l.ReceivedQty) || 0,
        })));
        axios.get(`${window.backendUrl}/purchase_vendors`, { params: { type: 'Supplier', category: purchase.PurchaseCategory, active: 1 } })
            .then(res => setVendors(res.data.data || [])).catch(console.error);
        if (isPaper) axios.get(`${window.backendUrl}/get_all_gsm`).then(res => setGsmOptions(res.data.data || [])).catch(console.error);
        else axios.get(`${window.backendUrl}/indirect_materials`, { params: { active: 1 } }).then(res => setMaterialOptions(res.data.data || [])).catch(console.error);
    }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

    const setField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

    const setRow = (idx, field, value) => {
        const next = [...rows];
        next[idx] = { ...next[idx], [field]: value };
        if (field === 'materialId') {
            const m = materialOptions.find(o => String(o.MaterialID) === String(value));
            if (m) next[idx].unit = m.UnitName;
        }
        setRows(next);
    };

    const addRow = () => setRows([...rows, isPaper
        ? { lineId: null, gsmId: '', widthInch: '', qty: '', unit: rows[rows.length - 1]?.unit || 'MT', unitPrice: '', received: 0 }
        : { lineId: null, materialId: '', qty: '', unit: 'kg', unitPrice: '', received: 0 }]);

    const removeRow = (idx) => setRows(rows.filter((_, i) => i !== idx));

    // Keep the vendor in the list even if it has since been marked inactive
    const vendorList = vendors.some(v => String(v.VendorID) === String(purchase.VendorID))
        ? vendors : [{ VendorID: purchase.VendorID, Name: purchase.VendorName }, ...vendors];

    const handleSave = async () => {
        setError('');
        const payload = {
            vendorId: form.vendorId, podate: toYmd(form.podate), currency: form.currency, paymentTerm: form.paymentTerm,
            neededByDate: toYmd(form.neededByDate), expectedArrivalDate: toYmd(form.expectedArrivalDate), remarks: form.remarks, transportCost: form.transportCost,
            lines: rows.map(r => (isPaper
                ? { lineId: r.lineId, gsmId: r.gsmId, widthInch: r.widthInch, description: r.description, qty: r.qty, unit: r.unit, unitPrice: r.unitPrice }
                : { lineId: r.lineId, materialId: r.materialId, description: r.description, qty: r.qty, unit: r.unit, unitPrice: r.unitPrice })),
        };
        if (isImport) Object.assign(payload, { incoterm: form.incoterm, portOfLoading: form.portOfLoading, portOfDischarge: form.portOfDischarge });
        setSaving(true);
        try {
            const res = await axios.put(`${window.backendUrl}/purchases/${purchase.PurchaseID}`, payload);
            onSaved(res.data);
        } catch (err) {
            console.error('Error saving purchase:', err);
            setError(err?.response?.data?.error || 'Could not save the changes.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton><Modal.Title>Edit {purchase.PONo}</Modal.Title></Modal.Header>
            <Modal.Body>
                {hasReceived && (
                    <Alert variant="info" className="py-2">
                        Goods are already received on this PO: the vendor is locked, and received lines can't be removed, change item/size/unit, or go below the received qty.
                    </Alert>
                )}
                <Row className="mb-3">
                    <Col md={6}>
                        <Form.Label>Vendor</Form.Label>
                        <Form.Select value={form.vendorId || ''} disabled={hasReceived} onChange={e => setField('vendorId', e.target.value)}>
                            {vendorList.map(v => <option key={v.VendorID} value={v.VendorID}>{v.Name}</option>)}
                        </Form.Select>
                    </Col>
                    <Col md={3}>
                        <Form.Label>PO Date</Form.Label>
                        <DatePicker value={form.podate} onChange={v => setField('podate', v)} oneTap cleanable={false} style={{ width: '100%' }} />
                    </Col>
                    <Col md={3}>
                        <Form.Label>Currency</Form.Label>
                        <Form.Control value={form.currency || ''} onChange={e => setField('currency', e.target.value)} />
                    </Col>
                </Row>
                <Row className="mb-3">
                    <Col md={3}>
                        <Form.Label>Payment term</Form.Label>
                        <Form.Control value={form.paymentTerm || ''} onChange={e => setField('paymentTerm', e.target.value)} />
                    </Col>
                    <Col md={3}>
                        <Form.Label>Transport cost</Form.Label>
                        <Form.Control type="number" min="0" value={form.transportCost || ''} onChange={e => setField('transportCost', e.target.value)} placeholder="0" />
                    </Col>
                    <Col md={3}>
                        <Form.Label>Needed by</Form.Label>
                        <DatePicker value={form.neededByDate} onChange={v => setField('neededByDate', v)} oneTap style={{ width: '100%' }} />
                    </Col>
                    <Col md={3}>
                        <Form.Label>Expected arrival</Form.Label>
                        <DatePicker value={form.expectedArrivalDate} onChange={v => setField('expectedArrivalDate', v)} oneTap style={{ width: '100%' }} />
                    </Col>
                </Row>
                {isImport && (
                    <Row className="mb-3">
                        <Col md={4}><Form.Label>Incoterm</Form.Label><Form.Control value={form.incoterm || ''} onChange={e => setField('incoterm', e.target.value)} /></Col>
                        <Col md={4}><Form.Label>Port of loading</Form.Label><Form.Control value={form.portOfLoading || ''} onChange={e => setField('portOfLoading', e.target.value)} /></Col>
                        <Col md={4}><Form.Label>Port of discharge</Form.Label><Form.Control value={form.portOfDischarge || ''} onChange={e => setField('portOfDischarge', e.target.value)} /></Col>
                    </Row>
                )}

                <hr />
                <Row className="align-items-center mb-2">
                    <Col><strong>Items</strong></Col>
                    <Col className="text-end"><Button size="sm" onClick={addRow}>+ Add Line</Button></Col>
                </Row>
                <Table size="sm" bordered>
                    <thead>
                        <tr>
                            <th>{isPaper ? 'Paper' : 'Material'}</th>
                            {isPaper && <th style={{ width: 90 }}>Width (inch)</th>}
                            <th style={{ width: 90 }}>Qty</th><th style={{ width: 80 }}>Unit</th>
                            <th style={{ width: 100 }}>{isPaper ? 'Rate / kg' : 'Unit price'}</th><th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, idx) => {
                            const locked = r.received > 0;
                            return (
                                <tr key={r.lineId || `new-${idx}`}>
                                    <td>
                                        {isPaper ? (
                                            <Form.Select size="sm" value={r.gsmId} disabled={locked} onChange={e => setRow(idx, 'gsmId', e.target.value)}>
                                                <option value="">Select paper</option>
                                                {gsmOptions.map(o => <option key={o.GSMID} value={o.GSMID}>{o.Name} {o.GSMValue}</option>)}
                                            </Form.Select>
                                        ) : (
                                            <Form.Select size="sm" value={r.materialId} disabled={locked} onChange={e => setRow(idx, 'materialId', e.target.value)}>
                                                <option value="">Select material</option>
                                                {materialOptions.map(o => <option key={o.MaterialID} value={o.MaterialID}>{o.Name}</option>)}
                                            </Form.Select>
                                        )}
                                        {locked && <div className="text-muted" style={{ fontSize: 12 }}>Received {r.received} {r.unit}</div>}
                                    </td>
                                    {isPaper && <td><Form.Control size="sm" value={r.widthInch} disabled={locked} onChange={e => setRow(idx, 'widthInch', e.target.value)} /></td>}
                                    <td><Form.Control size="sm" value={r.qty} onChange={e => setRow(idx, 'qty', e.target.value)} /></td>
                                    <td><Form.Control size="sm" value={r.unit} disabled={locked} onChange={e => setRow(idx, 'unit', e.target.value)} /></td>
                                    <td><Form.Control size="sm" value={r.unitPrice} onChange={e => setRow(idx, 'unitPrice', e.target.value)} /></td>
                                    <td className="text-center">
                                        {!locked && rows.length > 1 && <i className="fa fa-trash text-danger" style={{ cursor: 'pointer' }} onClick={() => removeRow(idx)}></i>}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
                <Form.Group className="mb-2">
                    <Form.Label>Remarks</Form.Label>
                    <Form.Control as="textarea" rows={2} value={form.remarks || ''} onChange={e => setField('remarks', e.target.value)} />
                </Form.Group>
                {error && <Alert variant="danger" className="py-2 mb-0">{error}</Alert>}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>Cancel</Button>
                <Button variant="primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save changes'}</Button>
            </Modal.Footer>
        </Modal>
    );
}

export default EditPurchaseModal;
